import { useCallback, useMemo } from 'react';
import { Box, Button, IconButton, Paper, Stack, Tooltip, Typography, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import type { ExchangeCartLine, ExchangeReturnDisposition } from './types';
import { usePersonLabels } from '@/hooks/usePersonLabels';

export interface ReturnCartPaneProps {
  cartLines: ExchangeCartLine[];
  signatureAcknowledged: boolean;
  signaturePreviewSrc?: string;
  onCaptureSignature: () => void;
  onClearSignature?: () => void;
  onShowCustomerSummary?: () => void;
  onRemovePair: (issuedItemId: string) => void;
  onCompleteExchange?: () => void;
  canComplete: boolean;
  isCompleting?: boolean;
}

const CONDITION_LABELS: Record<ExchangeReturnDisposition, string> = {
  recoverable: 'Serviceable',
  non_recoverable: 'Non-Serviceable',
};

const buildGroupKey = (line: ExchangeCartLine) =>
  [
    line.issuedItem.name,
    line.issuedItem.lin,
    line.issuedItem.nsn,
    line.replacementItem.name,
    line.replacementItem.lin,
    line.replacementItem.nsn,
    line.returnDisposition,
  ].join('::');

interface SummaryDetail {
  label: string;
  value: string;
}

const formatItemSummary = (
  label: string,
  name: string,
  lin: string,
  nsn: string,
): SummaryDetail => ({
  label,
  value: `${name} - ${lin} - ${nsn}`,
});

interface CartGroup {
  id: string;
  issuedSummary: SummaryDetail;
  replacementSummary: SummaryDetail;
  conditionLabel: string;
  quantity: number;
  issuedItemIds: string[];
  returnDisposition: ExchangeReturnDisposition;
}

export const ReturnCartPane = ({
  cartLines,
  signatureAcknowledged,
  signaturePreviewSrc,
  onCaptureSignature,
  onClearSignature,
  onShowCustomerSummary,
  onRemovePair,
  onCompleteExchange,
  canComplete,
  isCompleting = false,
}: ReturnCartPaneProps) => {
  const person = usePersonLabels('front_window');
  const groupedLines = useMemo<CartGroup[]>(() => {
    if (cartLines.length === 0) {
      return [];
    }

    const groups = new Map<string, CartGroup>();

    cartLines.forEach((line) => {
      const key = buildGroupKey(line);
      const existing = groups.get(key);

      if (existing) {
        existing.quantity += 1;
        existing.issuedItemIds.push(line.issuedItem.id);
        return;
      }

      groups.set(key, {
        id: key,
        issuedSummary: formatItemSummary(
          'Returned',
          line.issuedItem.name,
          line.issuedItem.lin,
          line.issuedItem.nsn,
        ),
        replacementSummary: formatItemSummary(
          'Replacement',
          line.replacementItem.name,
          line.replacementItem.lin,
          line.replacementItem.nsn,
        ),
        conditionLabel: CONDITION_LABELS[line.returnDisposition] ?? '—',
        quantity: 1,
        issuedItemIds: [line.issuedItem.id],
        returnDisposition: line.returnDisposition,
      });
    });

    return Array.from(groups.values());
  }, [cartLines]);

  const totals = useMemo(() => {
    return groupedLines.reduce(
      (accumulator, group) => {
        accumulator.quantity += group.quantity;
        if (group.returnDisposition === 'recoverable') {
          accumulator.serviceable += group.quantity;
        } else {
          accumulator.nonServiceable += group.quantity;
        }
        return accumulator;
      },
      { quantity: 0, serviceable: 0, nonServiceable: 0 },
    );
  }, [groupedLines]);

  const handleRemoveGroup = useCallback(
    (group: CartGroup) => {
      group.issuedItemIds.forEach((issuedId) => onRemovePair(issuedId));
    },
    [onRemovePair],
  );

  return (
    <Stack spacing={3} component="section">
      <Stack spacing={2}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Exchange Cart
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, maxHeight: '32vh', overflow: 'auto' }}>
          {groupedLines.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No items added to the exchange cart yet.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {groupedLines.map((group, index) => (
                <Stack key={group.id} spacing={1.5}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Stack spacing={0.5} sx={{ flexGrow: 1 }}>
                      <Typography variant="body2">
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          {group.issuedSummary.label}:
                        </Box>{' '}
                        {group.issuedSummary.value}
                      </Typography>
                      <Typography variant="body2">
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          {group.replacementSummary.label}:
                        </Box>{' '}
                        {group.replacementSummary.value}
                      </Typography>
                      <Typography variant="body2">
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          Replacement Condition:
                        </Box>{' '}
                        {group.conditionLabel}
                      </Typography>
                      <Typography variant="body2">
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          Quantity Returned:
                        </Box>{' '}
                        {group.quantity}
                      </Typography>
                    </Stack>
                    <IconButton
                      aria-label="Remove exchange group"
                      onClick={() => handleRemoveGroup(group)}
                      disabled={isCompleting}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  {index < groupedLines.length - 1 ? <Divider /> : null}
                </Stack>
              ))}
            </Stack>
          )}
        </Paper>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={0.5}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Total Line Items
          </Typography>
          <Typography variant="body2">
            <Box component="span" sx={{ fontWeight: 600 }}>
              Total Received:
            </Box>{' '}
            {totals.quantity}
          </Typography>
          <Typography variant="body2">
            <Box component="span" sx={{ fontWeight: 600 }}>
              Serviceable:
            </Box>{' '}
            {totals.serviceable}
          </Typography>
          <Typography variant="body2">
            <Box component="span" sx={{ fontWeight: 600 }}>
              Non-Serviceable:
            </Box>{' '}
            {totals.nonServiceable}
          </Typography>
        </Stack>
      </Paper>

      <Stack spacing={2}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Step 1 • Signature
        </Typography>
        {onShowCustomerSummary ? (
          <Button
            variant="outlined"
            fullWidth
            onClick={onShowCustomerSummary}
            disabled={cartLines.length === 0 || isCompleting}
          >
            View Exchange Summary
          </Button>
        ) : null}
        <Button variant="outlined" fullWidth onClick={onCaptureSignature} disabled={cartLines.length === 0 || isCompleting}>
          {signatureAcknowledged ? 'Signature Captured' : `Capture ${person.label} Signature`}
        </Button>
        {signaturePreviewSrc ? (
          <Stack spacing={0.5}>
            <Paper variant="outlined" sx={{ p: 1, bgcolor: '#ffffff' }}>
              <Box
                component="img"
                src={signaturePreviewSrc}
                alt="Captured signature preview"
                sx={{
                  display: 'block',
                  width: '100%',
                  maxHeight: 120,
                  objectFit: 'contain',
                }}
              />
            </Paper>
            {onClearSignature ? (
              <Button
                size="small"
                variant="text"
                color="inherit"
                onClick={onClearSignature}
                sx={{ alignSelf: 'flex-start' }}
              >
                Clear signature
              </Button>
            ) : null}
          </Stack>
        ) : null}
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Step 2 • Complete Exchange
        </Typography>
        <Tooltip
          title={
            isCompleting
              ? ''
              : cartLines.length === 0
                ? 'Add items to the exchange cart first'
                : !signatureAcknowledged
                  ? 'Complete Step 1 (Signature) first'
                  : !canComplete
                    ? 'Complete all exchange pairs first'
                    : ''
          }
          arrow
        >
          <span>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              onClick={onCompleteExchange}
              disabled={!canComplete || isCompleting}
            >
              {isCompleting ? 'Completing...' : 'Complete Exchange'}
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
};
